import type {
  CreateUnsettlementsActionRequest,
  FormattedUnsettlementsActions,
  GetUnsettlementsActionsAPIRequest,
  TransformedUnsettlementsActionsResponse,
  TransformedUnsettlementsActionsResponses,
  UnsettlementsActionsResponse,
  UnsettlementsAdminsResponse,
  UnsettlementsReasons
} from '@library/types/unsettlements';
import { concat, mergeDeepWith } from 'ramda';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { defaultHeaders } from '@bo-constants/apiHeaders';
import { transformUnsettlementsActionsResponse } from '@bo-utils/unsettlements';

const { memberSupport } = apiPaths;

const unsettlementActions = 'unsettlement_actions';

export const unsettlementsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // GET
    getUnsettlementsActions: build.query<
      FormattedUnsettlementsActions,
      GetUnsettlementsActionsAPIRequest
    >({
      query: ({ page, perPage }) => ({
        url: `${memberSupport}${unsettlementActions}`,
        method: 'GET',
        headers: defaultHeaders,
        params: {
          page,
          per_page: perPage
        }
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (
            a: TransformedUnsettlementsActionsResponse,
            b: TransformedUnsettlementsActionsResponse
          ) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as TransformedUnsettlementsActionsResponses;
            }
            return b;
          },
          currentCache,
          newItems
        ) as FormattedUnsettlementsActions;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      transformResponse: (
        response: UnsettlementsActionsResponse,
        _,
        { page, perPage }
      ) =>
        transformUnsettlementsActionsResponse({
          page,
          perPage,
          response
        }),
      providesTags: ['Unsettlements']
    }),

    getUnsettlementsAdmins: build.query<
      Array<UnsettlementsAdminsResponse>,
      void
    >({
      query: () => ({
        url: `${memberSupport}${unsettlementActions}/admins`,
        method: 'GET',
        headers: defaultHeaders
      }),
      transformResponse: (response: Array<UnsettlementsAdminsResponse>) =>
        response,
      providesTags: ['Unsettlements']
    }),

    getUnsettlementsReasons: build.query<Array<UnsettlementsReasons>, void>({
      query: () => ({
        url: `${memberSupport}${unsettlementActions}/reasons`,
        method: 'GET',
        headers: defaultHeaders
      }),
      transformResponse: (response: Array<UnsettlementsReasons>) => response,
      providesTags: ['Unsettlements']
    }),
    // POST
    createUnsettlementsAction: build.mutation<
      UnsettlementsActionsResponse,
      CreateUnsettlementsActionRequest
    >({
      query: (payload) => ({
        url: `${memberSupport}${unsettlementActions}`,
        method: 'POST',
        body: payload,
        headers: defaultHeaders
      }),
      transformResponse: (response: UnsettlementsActionsResponse) => response
    })
  })
});

export const {
  useGetUnsettlementsActionsQuery,
  useGetUnsettlementsAdminsQuery,
  useGetUnsettlementsReasonsQuery,
  useCreateUnsettlementsActionMutation
} = unsettlementsApi;
